import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DeviceDetailsService } from '@core/services/device-details.service';
import { FirebaseNotificationsService } from '@core/services/firebase-notifications.service';
import { PartneroService } from '@core/services/partnero.service';
import { FaIconComponent, FaStackComponent, FaStackItemSizeDirective } from '@fortawesome/angular-fontawesome';
import { faCircle, faGlobe, faLifeRing, faSignOut, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { BrowserTracing } from '@sentry/angular-ivy';
import { ButtonPrimaryComponent } from '@shared/components/button-primary/button-primary.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { HeroFooterComponent } from '@shared/components/hero-footer/hero-footer.component';
import { SidebarComponent } from '@shared/components/sidebar/sidebar.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './core/auth.service';
import { HTTPStatus } from './core/progress.inteceptor';
import { BaseApiService } from './core/services/base-api.service';
import { shouldIgnoreSentryEvent } from './core/services/CustomErrorHandler';
import { KycService } from './core/services/kyc.service';
import { LanguageService } from './core/services/language.service';
import { UserService } from './core/services/user.service';
import { FeedbackPopupComponent } from './feedback-popup/feedback-popup.component';
import { SubscriptionPlansComponent } from './onboarding/subscription-plans/subscription-plans.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { LanguageSelectorComponent } from './shared/language-selector/language-selector.component';
import { AppError, AppErrorDetails } from './shared/models/app-errors';
import { ToastService } from './shared/services/toast/toast.service';

const SUPPORTED_LANGUAGE_CODES = ['en', 'no', 'es', 'se', 'fr', 'de', 'da', 'nl'] as const;
const BLOCKED_ACCOUNT_ERROR_CODES: string[] = ['00203', '00206'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    ToastComponent,
    RouterOutlet,
    HeroFooterComponent,
    MatProgressBar,
    NgClass,
    FaIconComponent,
    NgStyle,
    FaStackComponent,
    FaStackItemSizeDirective,
    SubscriptionPlansComponent,
    AsyncPipe,
    TranslateModule,
    HeaderComponent,
    SidebarComponent,
    LanguageSelectorComponent,
    FeedbackPopupComponent,
    ButtonPrimaryComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private translate = inject(TranslateService);
  private languageService = inject(LanguageService);
  private authService = inject(AuthService);
  private httpStatus = inject(HTTPStatus);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private baseService = inject(BaseApiService);
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private kycService = inject(KycService);
  private deviceService = inject(DeviceDetectorService);
  private toastService = inject(ToastService);
  private partneroService = inject(PartneroService);
  private deviceDetailsService = inject(DeviceDetailsService);
  private firebaseNotificationsService = inject(FirebaseNotificationsService);

  title = 'bueno-frontend';
  token = '';
  faLifeRing = faLifeRing;
  faSpinner = faSpinner;
  faGlobe = faGlobe;
  faCircle = faCircle;
  faSignOut = faSignOut;
  httpLoadingInProgress = new Observable<boolean>();
  showError = false;
  blockedAccountError = false;
  readonly supportUrl = 'https://getbueno.com/help/';
  emptyRouter = false;
  error!: AppError;
  errorDetails: any;
  timeoutCounter: any;
  authenticated = false;
  subscriptionDisabled = false;
  showInvoiceDetails = false;
  isMobile = window.innerWidth <= 770;
  language!: string;
  langMenu = false;
  profileMenu = false;
  moreActionsMenu = false;
  blockSubscriptionButton = false;
  enableReload = false;
  showLogoutQuestion = false;
  maintenance: boolean;
  useWhiteBackground = false;
  paymentForm: FormGroup;
  hideFooter = false;
  showNativeAppAdd = false;
  emptyRouterRoutes = [
    '/succesfuly-pin-changed',
    '/404',
    '/user/register-user',
    '/landing',
    '/user/forgot-password',
    '/payments/success',
    '/payments/success-invoice',
    '/payments/cancel',
  ];
  hideFooterRoutes = ['/user/register-user', '/landing'];
  whiteBackgroundRoutes = [
    '/onboarding',
    '/transactions',
    '/account',
    '/contacts',
    '/cards',
    '/user/forgot-password',
    '/properties',
    '/insurance',
    '/direct-debit',
  ];
  downloadInProgress: boolean = false;
  showErrorTranslation: boolean = false;
  showErrorDescription: boolean = false;
  onboarded: boolean = false;
  showDirectDebitInfo: boolean = false;
  private destroy$ = new Subject<void>();
  isOnboardingRoute: boolean = false;
  private deviceDetailsStored = false;
  private deviceDetailsRegistrationInProgress = false;
  private lastRegisteredFirebaseToken: string | null = null;

  readonly toastComponent = viewChild.required<ToastComponent>('toast');
  sideBarOpen: boolean = false;
  userName: string = '';
  windowWidth: number = window.innerWidth;
  userMail: string = '';
  referral_url: any;
  loaded: boolean = false;
  hasPropertiesSubscription: boolean = false;
  hasValidSubscription: boolean = false;

  constructor() {
    this.paymentForm = this.formBuilder.group({
      period: ['', [Validators.required]],
    });
    this.checkRetina();
    this.processLanguageSettings();
    this.checkDevice();
    if (environment.production) {
      this.configureSentry();
    }
    this.initializeLanguageService();
    this.maintenance = environment.underMaintenance;
    if (!this.maintenance) {
      this.authService.runInitialLoginSequence().then(() => {
        const latestUrl = localStorage.getItem('currentUrl');
        if (latestUrl?.includes('/?code=')) {
          this.router.navigate(['/home']);
        }
      });
    } else {
      this.enableReload = true;
    }
    this.subscribeToAuthService();
    this.subscribeToFirebaseTokenChanges();
    this.subscribeToHttpStatus();
    this.subscribeToRouterEvents();
  }

  checkRetina() {
    if (window.devicePixelRatio > 1) {
      document.body.classList.add('retina-display');
      document.body.classList.add(`pixel-ratio-${Math.floor(window.devicePixelRatio)}`);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.windowWidth = (event.target as Window).innerWidth;
    this.controlZoom(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.startLogoutTimer();
    if (localStorage.getItem('selectedLanguage')) {
      this.language = this.normalizeLanguageCode(localStorage.getItem('selectedLanguage'));
    }
  }

  ngAfterViewInit() {
    this.toastService.registerToast(this.toastComponent());
  }

  private initializeLanguageService(): void {
    this.languageService
      .getLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        const normalizedLang = this.normalizeLanguageCode(lang);

        this.language = normalizedLang;
        localStorage.setItem('selectedLanguage', normalizedLang);
        if (this.authenticated) {
          this.userService
            .changePreferredLanguage(normalizedLang)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.translate.use(normalizedLang);
            });
        } else {
          this.translate.use(normalizedLang);
        }
      });
  }

  private subscribeToAuthService(): void {
    this.authService.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(
      authenticated => {
        // Removed comment
        if (this.authenticated !== authenticated) {
          this.authenticated = authenticated;
          window.configureChatwoot?.(this.authenticated);
          if (!this.authenticated) {
            this.deviceDetailsStored = false;
            this.deviceDetailsRegistrationInProgress = false;
            this.lastRegisteredFirebaseToken = null;
          }
          if (this.authenticated) {
            this.userService
              .getUserInfo()
              .pipe(takeUntil(this.destroy$))
              .subscribe(user => {
                // Removed comment
                this.loaded = true;
                if (user.created) {
                  this.userName = user.firstName;
                  this.userMail = user.email;
                  this.storeDeviceDetailsAfterLogin();
                  this.setUserInfoInSentryAndMouseflow(user);
                  this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
                    // Removed comment
                    if (params['kyc']) {
                      this.kycService
                        .setKycPending()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(() => {
                          // Removed comment
                          this.userService
                            .updateUserInfo()
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(() => {
                              // Removed comment
                              this.router.navigate(['']);
                            });
                        });
                    }
                  });
                  if (user.preferredLanguage) {
                    const lang = this.normalizeLanguageCode(user.preferredLanguage);
                    this.setLanguage(lang);
                    localStorage.setItem('selectedLanguage', lang);
                  }
                  this.subscriptionDisabled = user.partnerDetails?.isActive
                    ? false
                    : this.isSubscriptionDisabled(user.subscriptionsHistory ?? []);
                  this.onboarded = this.checkOnboardedUser(user);
                  if (
                    this.onboarded &&
                    !user.partnerDetails?.isActive &&
                    (user.subscriptionsHistory?.length ?? 0) === 0
                  ) {
                    this.subscriptionDisabled = true;
                  }
                  if (this.onboarded) {
                    this.checkDDApplications();
                    this.getReferralLink();
                  }
                }
              });
          }
        }
      },
      () => {
        this.router.navigate(['/landing']);
      }
    );
  }

  private subscribeToFirebaseTokenChanges(): void {
    this.firebaseNotificationsService.initialize();

    this.firebaseNotificationsService.token$
      .pipe(
        takeUntil(this.destroy$),
        filter((token): token is string => Boolean(token && token.trim())),
        distinctUntilChanged()
      )
      .subscribe(token => {
        if (!this.authenticated || token === this.lastRegisteredFirebaseToken) {
          return;
        }

        this.deviceDetailsStored = false;
        this.storeDeviceDetailsAfterLogin(token);
      });
  }

  getReferralLink() {
    this.partneroService
      .getPartneroCustomer(this.userMail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (response && response.data.referral_link) {
          this.referral_url = response.data.referral_link;
        }
      });
  }

  private checkDDApplications() {
    this.userService
      .getDirectDebitApplications()
      .pipe(takeUntil(this.destroy$))
      .subscribe(applications => {
        // The sidebar hint stays reachable until the user has their first direct debit.
        this.showDirectDebitInfo = applications.length === 0;
      });
  }

  private isSubscriptionDisabled(subscriptionsHistory: any[]): boolean {
    if (subscriptionsHistory.length === 0) {
      return false;
    }
    const activeSubscription = subscriptionsHistory.find(subscription => subscription.isActive);
    if (!activeSubscription) {
      return true;
    }
    const paidUntilDate = new Date(activeSubscription.paidUntil);
    const currentDate = new Date();
    return paidUntilDate < currentDate;
  }

  private checkOnboardedUser(user: any): boolean {
    const hasActivePartner = !!user.partnerDetails?.isActive;
    const subscriptionsHistory = hasActivePartner ? [] : (user.subscriptionsHistory ?? []);
    this.hasPropertiesSubscription = subscriptionsHistory
      .filter((x: { isActive: boolean }) => x.isActive)
      .some((subscription: { stripePriceId: string }) => {
        return subscription.stripePriceId === environment.subscriptionProducts.properties;
      });
    this.hasValidSubscription = hasActivePartner || subscriptionsHistory.some((x: { isActive: boolean }) => x.isActive);
    return (
      (hasActivePartner || subscriptionsHistory.length > 0) &&
      !!user.providerUserInfos?.some((u: { ledgerToken: any; iban: any }) => u.ledgerToken && u.iban)
    );
  }

  private subscribeToHttpStatus(): void {
    this.httpStatus
      .getHttpStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        // Removed comment
        this.httpLoadingInProgress = new Observable<boolean>(observer => {
          this.downloadInProgress = localStorage.getItem('downloadInProgress') === 'true';
          let usingOnboarding = localStorage.getItem('onboardingStep') !== null;
          let usingInfiniteScroll = localStorage.getItem('usingInfiniteScroll') === 'true';
          observer.next(status && !usingInfiniteScroll && !usingOnboarding);
        });
      });
    this.baseService.currentError.pipe(takeUntil(this.destroy$)).subscribe(error => {
      // Removed comment
      if (error && error.errorDetails) {
        this.handleErrors(error.errorDetails);
      }
    });
  }

  private subscribeToRouterEvents(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(evt => {
      // Ensure we react to the final URL after any redirects
      if (evt instanceof NavigationEnd) {
        this.handleRouterEvents(evt.urlAfterRedirects);
      }
    });
  }

  private handleErrors(errorDetails: AppErrorDetails[]): void {
    if (errorDetails.length === 0) {
      errorDetails = [new AppErrorDetails('CONNECTION_ERROR', '')];
      this.enableReload = true;
    } else if (errorDetails[0].code === 'SEFIDECRITERR') {
      this.enableReload = true;
    }
    this.blockedAccountError = errorDetails.some(errorDetail => BLOCKED_ACCOUNT_ERROR_CODES.includes(errorDetail.code));
    if (errorDetails[0].code !== 'EMPTY_ERROR') {
      this.errorDetails = errorDetails;
      this.checkIfErrorTranslationExists(errorDetails[0].code, errorDetails[0].description);
      this.showError = true;
    }
  }

  checkIfErrorTranslationExists(key: string, description: string) {
    const translationKey = `ERROR_CODES.${key}`;
    const translation = this.translate.instant(translationKey);
    const hasTranslation = translation !== translationKey;

    this.showErrorTranslation = hasTranslation;
    this.showErrorDescription = !hasTranslation && !this.containsUrl(description);
  }

  private containsUrl(value: string): boolean {
    if (!value) {
      return false;
    }
    return /(https?:\/\/|www\.)\S+/i.test(value);
  }

  private handleRouterEvents(url: string): void {
    localStorage.setItem('currentUrl', url);
    this.emptyRouter = this.emptyRouterRoutes.some(route => url.includes(route));
    this.hideFooter = this.hideFooterRoutes.some(route => url.includes(route));
    this.useWhiteBackground = this.whiteBackgroundRoutes.some(route => url.includes(route));
    this.isOnboardingRoute = url.includes('/onboarding');

    this.controlZoom(url);

    if (url.includes('/onboarding')) {
      this.showNativeAppAdd = false;
    }
  }

  private controlZoom(url: string): void {
    const htmlElement = document.documentElement;

    if (url.includes('/user/register-user') || url.includes('/user/forgot-password')) {
      htmlElement.style.setProperty('zoom', '1', 'important');
      htmlElement.style.setProperty('--viewport-scale', '1', 'important');
      htmlElement.style.setProperty('width', 'auto', 'important');
      const appContainers = document.querySelectorAll('.app-root-container, .empty-router');
      appContainers.forEach(container => {
        (container as HTMLElement).style.setProperty('width', 'auto', 'important');
      });
    } else {
      htmlElement.style.removeProperty('zoom');
      htmlElement.style.removeProperty('--viewport-scale');
      htmlElement.style.removeProperty('width');

      const isRetina = window.devicePixelRatio > 1.5;
      const isDesktop = window.matchMedia('(pointer: fine)').matches;
      const isInViewportRange = window.matchMedia('(max-width: 1919px) and (min-width: 768px)').matches;
      const viewportScale = Math.min(window.innerWidth / 1920, 1);
      if (isRetina && isDesktop) {
        htmlElement.style.setProperty('height', '100%');
        htmlElement.style.setProperty('zoom', viewportScale.toString());
      } else if (isInViewportRange && isDesktop) {
        htmlElement.style.setProperty('zoom', viewportScale.toString());
        htmlElement.style.setProperty('--viewport-scale', viewportScale.toString());
        htmlElement.style.setProperty('width', '1920px');
      }
      const appContainers = document.querySelectorAll('.app-root-container, .empty-router');
      appContainers.forEach(container => {
        (container as HTMLElement).style.removeProperty('width');
        if (isInViewportRange && isDesktop) {
          (container as HTMLElement).style.setProperty('width', '1920px');
        }
      });
    }
  }

  setLanguage(lang: string): void {
    const normalizedLang = this.normalizeLanguageCode(lang);

    this.language = normalizedLang;
    this.languageService.setLanguage(normalizedLang);
    this.langMenu = false;
    this.profileMenu = false;
    this.moreActionsMenu = false;
  }

  toggleLangMenu(): void {
    this.langMenu = !this.langMenu;
    this.moreActionsMenu = false;
    this.profileMenu = false;
  }

  logout(): void {
    this.authService.logout();
  }

  startLogoutTimer(): void {
    clearTimeout(this.timeoutCounter);
    this.showLogoutQuestion = false;

    // Skip timer for localhost development
    if (window.location.href.includes('localhost:4200')) {
      return;
    }

    this.timeoutCounter = setTimeout(() => {
      if (this.authenticated) {
        this.askLogout();
      }
    }, 4 * 60000);
  }

  askLogout(): void {
    clearTimeout(this.timeoutCounter);
    this.showLogoutQuestion = true;
    this.timeoutCounter = setTimeout(() => {
      if (this.authenticated) {
        localStorage.setItem('autoLogout', 'true');
        this.authService.logout();
      }
    }, 1 * 60000);
  }

  reload(): void {
    window.location.reload();
  }

  openSupport(): void {
    window.open(this.supportUrl, '_blank', 'noopener,noreferrer');
  }

  // Trigger yearly payment using Stripe
  yearlyPayment(): void {
    this.userService
      .stripeSubscription(environment.subscriptionProducts.annual)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        window.location.href = session.url;
      });
  }

  // Trigger quarterly payment using Stripe
  quarterlyPayment(): void {
    this.userService
      .stripeSubscription(environment.subscriptionProducts.quarterly)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        window.location.href = session.url;
      });
  }

  // Trigger monthly payment using Stripe
  monthlyPayment(): void {
    this.userService
      .stripeSubscription(environment.subscriptionProducts.month)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        window.location.href = session.url;
      });
  }

  subscribeUsingOdoo(productId: number, planId: number): void {
    this.userService
      .subscribeUsingOdoo(productId, planId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      });
  }

  redirectToAppStore(): void {
    const os = this.deviceService.getDeviceInfo().os;
    const storeUrl = os === 'iOS' ? environment.appStoreUrl : os === 'Android' ? environment.playStoreUrl : null;
    if (storeUrl) {
      setTimeout(() => {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 14);
        localStorage.setItem('showNativeAppAdd', JSON.stringify({ value: 'false', expiration: expirationDate }));
        window.open(storeUrl, '_blank');
      });
    }
  }

  closeAppAdd(): void {
    this.showNativeAppAdd = false;
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 14);
    localStorage.setItem('showNativeAppAdd', JSON.stringify({ value: 'false', expiration: expirationDate }));
  }

  private configureSentry(): void {
    Sentry.init({
      dsn: 'https://e352be7e415e4d649697f9e7275cedef@o1222038.ingest.sentry.io/6365646',
      autoSessionTracking: false,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.routingInstrumentation,
        }),
        new Sentry.Replay({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
        }),
      ],
      environment: environment.production ? 'production' : 'development',
      tracesSampleRate: 1.0,
      replaysOnErrorSampleRate: 0.3,
      replaysSessionSampleRate: 0,
      beforeSend(event) {
        if (shouldIgnoreSentryEvent(event)) {
          return null;
        }

        return event;
      },
    });
  }

  private checkDevice(): void {
    const isMobile = this.deviceService.isMobile();
    const isTablet = this.deviceService.isTablet();
    const showNativeAppAdd = localStorage.getItem('showNativeAppAdd');
    if (showNativeAppAdd) {
      const { value, expiration } = JSON.parse(showNativeAppAdd);
      const isExpired = new Date(expiration) < new Date();
      if (value !== 'false' || isExpired) {
        this.showNativeAppAdd = isMobile || isTablet;
      }
    } else {
      this.showNativeAppAdd = isMobile || isTablet;
    }
  }

  private setUserInfoInSentryAndMouseflow(user: any): void {
    const sentryUser = {
      id: user.id,
      email: user.email,
      username: user.email,
      name: `${user.firstName} ${user.lastName}`,
      language: user.preferredLanguage,
    };
    Sentry.setUser(sentryUser);
    // UXWizz is only loaded by the index files that include it; elsewhere this is a no-op
    window.UST?.addTag(user.email);
    if (environment.production) {
      // @ts-ignore
      window._mfq.push(['setVariable', 'id', user.id]);
      // @ts-ignore
      window._mfq.push(['setVariable', 'email', user.email]);
    }
  }

  private storeDeviceDetailsAfterLogin(firebaseToken?: string): void {
    if (this.deviceDetailsStored || this.deviceDetailsRegistrationInProgress) {
      return;
    }

    this.deviceDetailsRegistrationInProgress = true;
    this.deviceDetailsService
      .storeDeviceDetails(undefined, firebaseToken)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.deviceDetailsRegistrationInProgress = false;
        })
      )
      .subscribe({
        next: stored => {
          this.deviceDetailsStored = stored;
          if (stored) {
            this.lastRegisteredFirebaseToken = this.getCurrentFirebaseToken();
          }
        },
        error: () => {
          this.deviceDetailsStored = false;
        },
      });
  }

  private getCurrentFirebaseToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem('bueno.notifications.token');
    } catch {
      return null;
    }
  }

  processLanguageSettings(): void {
    this.translate.addLangs([...SUPPORTED_LANGUAGE_CODES]);
    this.translate.setDefaultLang('en');
    const browserLang = this.normalizeLanguageCode(this.translate.getBrowserLang());
    const storedLanguage = localStorage.getItem('selectedLanguage');
    const lang = storedLanguage ? this.normalizeLanguageCode(storedLanguage) : browserLang;

    if (storedLanguage) {
      if (storedLanguage !== lang) {
        localStorage.setItem('selectedLanguage', lang);
      }
    } else {
      localStorage.setItem('selectedLanguage', lang);
    }

    this.language = lang;
    this.translate.use(lang);
  }

  @HostListener('click')
  handleClick(): void {
    this.startLogoutTimer();
  }

  onToggleSidebar(event: any) {
    this.sideBarOpen = event;
  }

  private normalizeLanguageCode(lang?: string | null): string {
    const normalizedLang = (lang ?? '').trim().toLowerCase();

    if (normalizedLang === 'gb') {
      return 'en';
    }

    if (normalizedLang === 'nb') {
      return 'no';
    }

    if ((SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(normalizedLang)) {
      return normalizedLang;
    }

    return 'en';
  }
}
